import type { Afectado, Cierre, DiaEspecial } from '../../api/ops/disponibilidad';
import type { NombreIcono } from '../../componentes/iconos';
import type { MarcaDeslizador } from '../../componentes/Deslizador';
import { DIAS, diaDeLaSemana, fechaCorta, fechaLarga, rangoDeTramo, textoTramo } from '../../logica/horario';
import type { TarjetaAyuda } from '../../marco/cabecera';

/**
 * Textos de «Horario» (3.1), «Excepciones y festivos» (3.2) y la ventana «Revisar el impacto y confirmar» (3.4), tal cual
 * del diseño (`diseno/disponibilidad/3.1-3.2-3.4-horario-excepciones-y-afectados.html`) salvo donde una decisión lo
 * corrige:
 * - G246: los tramos no llevan nombre y se dice «tramo», nunca «turno»; la versión programada se lee por su fecha.
 * - G214: las palabras del negocio (`vocabulary`) y, si faltan, neutras («sitio», «reservas»); nada de «mesa» fijo.
 * - G121, G140: sin cifras sacadas de listas («Hay cierres ahora», «Ver todos los festivos»). La única cifra es
 *   `affected_count` de la vista previa.
 * - G253, G254: cada afectado con su `holder_label` y «qué pasa después» según el calendario que cambia.
 * - Los cierres y retenes se hacen en «Cierre de recursos» y en Sala (8.8 y 8.7), aquí solo se consultan.
 */
export const PERMISO_VER = 'disponibilidad.horarios.ver';
export const PERMISO_GESTIONAR = 'disponibilidad.horarios.gestionar';
export const PERMISO_NEGOCIO = 'disponibilidad.calendario_negocio.gestionar';
export const PERMISO_CONFIRMAR = 'disponibilidad.cambios.confirmar';

/** Marcas del deslizador: de las 05:00 a las 07:00 del día siguiente. */
export const MARCAS: readonly MarcaDeslizador[] = [
    { valor: 360, texto: '06:00' },
    { valor: 720, texto: '12:00' },
    { valor: 1080, texto: '18:00' },
    { valor: 1440, texto: '00:00', icono: 'moon' },
    { valor: 1800, texto: '06:00' },
];

/** El tramo que sale al añadir un horario o un día con horario especial: 20:00 a 00:00 (en citas, 09:00 a 14:00). */
export const tramoPorDefecto = (citas: boolean) => (citas ? { desde: 540, hasta: 840 } : { desde: 1200, hasta: 1440 });

export const AYUDA_HORARIO = (citas: boolean): TarjetaAyuda[] => [
    { icono: 'clock', titulo: 'Un horario, varios días', texto: 'Elige los días y arrastra «Desde» y «Hasta». Si otros días abren a otra hora, añade otro horario.' },
    citas
        ? { icono: 'sun', titulo: 'Pausa de mediodía', texto: 'Si cierras a mediodía, añade un tramo: de 9:00 a 14:00 y de 16:00 a 20:00.' }
        : { icono: 'moon', titulo: 'Pasada la medianoche', texto: 'De 21:00 a 03:00 se hace de un tirón: termina al día siguiente y cuenta para el día en que empieza.' },
    { icono: 'calendar-x', titulo: 'Excepciones y festivos', texto: 'Debajo: los días que cierras o abres con otro horario. Mandan sobre la semana.' },
];

/** Las cuatro tarjetas de la «?» de «Cuándo se puede reservar» (3.3, G812); en un negocio de citas, las suyas. */
export const AYUDA_REGLA = (citas: boolean): TarjetaAyuda[] =>
    citas
        ? [
              { icono: 'calendar-clock', titulo: 'Como pronto', texto: 'Lo más lejos que se puede pedir cita: con 90 días, hoy se puede pedir hasta dentro de tres meses.' },
              { icono: 'hourglass', titulo: 'Como tarde', texto: 'El margen que necesitas antes de cada cita. Vale para cualquier día menos hoy.' },
              { icono: 'sun', titulo: 'El mismo día', texto: 'Para hoy puedes dejar pedir cita hasta la hora, cortar unos minutos antes o solo antes de abrir.' },
              { icono: 'layers', titulo: 'Servicios con su propia regla', texto: 'Un servicio con otros plazos se cambia en su ficha: Servicios.' },
          ]
        : [
              { icono: 'calendar-clock', titulo: 'Como pronto', texto: 'Lo más lejos que se puede reservar: con 90 días, hoy se puede reservar hasta dentro de tres meses.' },
              { icono: 'hourglass', titulo: 'Como tarde', texto: 'El margen que necesitas para preparar la reserva. Vale para cualquier día menos hoy.' },
              { icono: 'sun', titulo: 'El mismo día', texto: 'Para hoy puedes dejar reservar hasta la hora de la reserva, cortar unos minutos antes o solo antes de abrir.' },
              { icono: 'layers', titulo: 'Zonas con su propia regla', texto: 'Una zona o un recurso con otros plazos se cambia en su ficha: Recursos.' },
          ];

export const TIPOS_DIA: readonly { tipo: DiaEspecial['kind']; icono: NombreIcono; nombre: string; ejemplo: string }[] = [
    { tipo: 'closed', icono: 'calendar-x', nombre: 'Cerrado', ejemplo: 'Nadie puede reservar ese día.' },
    { tipo: 'special_hours', icono: 'calendar-clock', nombre: 'Horario especial', ejemplo: 'Abre con otros tramos. Sirve para abrir un día que cierras.' },
    { tipo: 'holiday', icono: 'flag', nombre: 'Festivo', ejemplo: 'Una fiesta que no está en la lista oficial: la local, la del barrio…' },
];

export const NOMBRE_TIPO: Readonly<Record<DiaEspecial['kind'], string>> = { closed: 'Cerrado', special_hours: 'Horario especial', holiday: 'Festivo' };

/** Palabras del negocio (G214): la de una reserva o cita y si es un negocio de citas. */
export interface Palabras {
    citas: boolean;
    /** «reserva» o «cita» (o lo que diga el vocabulario del negocio). */
    compromiso: string;
}

export function palabrasDe(vocabulario: Readonly<Record<string, string>> | undefined, citas: boolean): Palabras {
    const propia = vocabulario?.appointment?.trim();
    return { citas, compromiso: propia !== undefined && propia !== '' ? propia.toLocaleLowerCase('es') : citas ? 'cita' : 'reserva' };
}

/** «reservas», «citas»: el plural de una palabra en español (vocal + s, consonante + es). */
export function plural(palabra: string): string {
    return /[aeiouáéó]$/i.test(palabra) ? `${palabra}s` : `${palabra}es`;
}

const mayuscula = (t: string) => t.charAt(0).toLocaleUpperCase('es') + t.slice(1);

/** «4 reservas ya hechas quedan fuera» / «1 cita ya pedida queda fuera» (la cifra la pinta RV Cifra). */
export function textoCifra(n: number, p: Palabras): string {
    const hechas = p.citas ? 'pedida' : 'hecha';
    return n === 1 ? `${p.compromiso} ya ${hechas} queda fuera` : `${plural(p.compromiso)} ya ${hechas}s quedan fuera`;
}

/** Qué pasa después de confirmar (G254): según cambie una sede o todo el negocio. */
export function queDespues(todoElNegocio: boolean, p: Palabras): { icono: NombreIcono; titulo: string; texto: string }[] {
    const modulo = p.citas ? 'Citas' : 'Reservas';
    const otro = p.citas ? 'otro profesional' : 'otro sitio';
    return [
        { icono: 'lock', titulo: 'Siguen guardadas', texto: 'Nadie más puede ocupar esos huecos mientras tanto.' },
        todoElNegocio
            ? { icono: 'bell', titulo: 'Te las dejamos en Avisos', texto: 'No hay otro sitio abierto: te dejamos la lista en Avisos para que llames a cada cliente (otra hora o cancelar con devolución íntegra).' }
            : { icono: 'refresh-cw', titulo: `${modulo} las recoloca`, texto: `Intenta pasarlas a ${otro} a la misma hora y avisa a cada cliente.` },
        { icono: 'history', titulo: 'Queda en Actividad', texto: 'Quién hizo el cambio y cuándo.' },
    ];
}

/** Aviso tras confirmar (sin «Deshacer», G159). */
export function textoConfirmado(n: number, todoElNegocio: boolean, p: Palabras): string {
    const cuales = n === 1 ? `la ${p.compromiso} afectada` : `las ${String(n)} ${plural(p.compromiso)} afectadas`;
    if (todoElNegocio) {
        return `Cambio guardado. Te dejamos en Avisos ${cuales} para que llames a cada cliente.`;
    }
    return `Cambio guardado. ${p.citas ? 'Citas' : 'Reservas'} intenta pasar ${cuales} a ${p.citas ? 'otro profesional' : 'otro sitio'} a la misma hora y avisa a cada cliente.`;
}

/** Una línea de la lista de afectados: «Sáb 17 oct · 14:00» y «Reserva R-7KT1 · Terraza» (`holder_label`, G253). */
export function lineaAfectado(a: Afectado, p: Palabras, anio: number): { icono: NombreIcono; cuando: string; que: string } {
    const cita = a.holder_module === 'citas' || (a.holder_module === undefined && p.citas);
    const cuando = a.local_date === undefined ? '' : `${fechaCorta(a.local_date, anio)}${a.local_time === undefined ? '' : ` · ${a.local_time}`}`;
    const que = a.holder_label != null && a.holder_label !== '' ? a.holder_label : mayuscula(cita ? 'cita' : p.compromiso);
    return { icono: cita ? 'calendar-check' : 'book-open', cuando, que };
}

/** «Horario especial · de 20:00 a 06:00 (+1) · sábado», «Festivo · cerrado · viernes», «Cerrado · del … al …». */
export function lineaDia(e: DiaEspecial, anio: number): string {
    const que = e.kind === 'special_hours' ? `Horario especial · ${(e.intervals ?? []).map((t) => textoTramo(rangoDeTramo(t))).join(' y ')}` : e.kind === 'holiday' ? 'Festivo · cerrado' : 'Cerrado';
    const cuando = e.start_date === e.end_date ? (DIAS[diaDeLaSemana(e.start_date)] ?? '') : `del ${fechaLarga(e.start_date, anio)} al ${fechaLarga(e.end_date, anio)}`;
    return `${que} · ${cuando}`;
}

/** «sábado 31 de octubre» o «del 2 al 3 de noviembre». */
export function fechasDe(desde: string, hasta: string, anio: number): string {
    return desde === hasta ? fechaLarga(desde, anio) : `del ${fechaLarga(desde, anio)} al ${fechaLarga(hasta, anio)}`;
}

export const ESTADO_CIERRE: Readonly<Record<Cierre['status'], { texto: string; estado: 'confirmed' | 'neutral' | 'problem' }>> = {
    active: { texto: 'Activo', estado: 'confirmed' },
    reopened: { texto: 'Reabierto', estado: 'neutral' },
    ended: { texto: 'Terminado', estado: 'neutral' },
    expired: { texto: 'Caducado', estado: 'problem' },
};

export const MOTIVO_CIERRE: Readonly<Record<Cierre['reason_kind'], { texto: string; icono: NombreIcono }>> = {
    breakdown: { texto: 'Avería', icono: 'armchair' },
    private_event: { texto: 'Evento privado', icono: 'party-popper' },
    weather: { texto: 'Tiempo', icono: 'umbrella' },
    door_hold: { texto: 'Retén de puerta', icono: 'door-open' },
    other: { texto: 'Otro', icono: 'lock' },
};

export const TEXTO_CONFLICTO_DIA = 'Alguien ha cambiado este día especial mientras tanto. Lo he vuelto a cargar: revisa y guarda otra vez.';
export const TEXTO_CONFLICTO_ZONA = 'Alguien ha cambiado este horario mientras tanto. Lo he vuelto a cargar: revisa y cambia la zona otra vez.';
